import type { IComponentController, IScope } from 'angular';
import template from './widget-instance-actions.component.html';
import { DashboardService, WidgetInstance } from '../dashboard.service';
import { DashboardPageComponent, DashboardPageController } from '../dashboard-page/dashboard-page.component';
import type { I18n } from '../../i18n/mx-internationalization';
import { ActionContextType, ActionsContext } from '../../Shell/Pandora/model/action';
import type { MxActionManagerService } from '../../Shell/Pandora/services/mx-action-manager-service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { SidePanelService } from '../../Shell/Pandora/services/mx-side-panel-service';
import type { WizardService } from '../../Shell/PandoraSolutionBuilderAgent/services/wizard-service';
import { WizardMode } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';
import type { WidgetInstanceAction } from '../widget-instance.model';
import type { NavigationItem } from '../../Navigation/model/navigation-item';

export class WidgetInstanceActionsController implements IComponentController {
	page!: DashboardPageController;
	actions: WidgetInstanceAction[] = [];
	popupOpened: boolean = false;
	widgetInstance!: WidgetInstance;
	private dashboardNavigationItems: NavigationItem[] = [];

	static readonly $inject = [
		'mx.internationalization',
		'mx.shell.ActionManagerService',
		'mx.shell.NotificationService',
		'mx.shell.SidePanelService',
		'mx.SolutionBuilderAgent.WizardService',
		'mx.shell.EventService',
		'mx.sb.Dashboard.Service',
		'$scope',
	];

	constructor(
		private readonly i18n: I18n,
		private readonly actionManagerService: MxActionManagerService,
		private readonly notificationService: NotificationService,
		private readonly sidePanelService: ReturnType<typeof SidePanelService>,
		private readonly wizardService: ReturnType<typeof WizardService>,
		private readonly eventService: EventService,
		private readonly dashboardService: DashboardService,
		$scope: IScope
	) {
		$scope.$on('$mdMenuOpen', () => (this.popupOpened = true));
		$scope.$on('$mdMenuClose', () => setTimeout(() => (this.popupOpened = false), 10));
	}

	$onInit() {
		this.dashboardNavigationItems = this.dashboardService
			.getDashboardNavigationItems()
			.filter((navItem) => navItem.id !== this.page.dashboard.navigationId)
			.sort((a, b) => a.title.localeCompare(b.title));

		this.initActions();
	}

	private async getOriginalShareAction() {
		const actionContext = new ActionsContext(['PDRWidgetInstanceType'], []);

		const selectedObjects = [
			{
				_id: 'PDRWidgetInstanceType',
				_type: 'PDRWidgetInstanceType',
				_displayName: 'PDRWidgetInstanceType',
			},
		];
		const actions = this.actionManagerService.filterActions(
			await this.actionManagerService.getActions(actionContext, [ActionContextType.Single], selectedObjects),
			undefined,
			selectedObjects
		);
		return actions.find((action) => action.name.startsWith('UUX.Client.WidgetInstance.Share'));
	}

	private async getShareAction(): Promise<WidgetInstanceAction> {
		return {
			icon: 'supervisor_account',
			label: this.i18n.get('solutionBuilderAgent.common.share'),
			hidden: this.widgetInstance.isEditable || Boolean(await this.getOriginalShareAction()),
			onItemClick: () => {
				const wizardView = this.wizardService.getWizardView({
					widgetId: 'a54d3c0e-204f-ef11-0486-ec63d7895110',
					wizardMode: WizardMode.Default,
					name: this.i18n.get('solutionBuilderAgent.common.share'),
					displayName: this.i18n.get('solutionBuilderAgent.common.share'),
					selectedObjects: [
						{
							_id: this.widgetInstance.id,
							_type: 'PDRWidgetInstanceType',
							_displayName: this.widgetInstance.title,
						},
					],
				});

				// @ts-expect-error no types
				wizardView.id = Date.now();
				this.sidePanelService.addView(wizardView);

				this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
					eventName: `Action clicked`,
					sender: 'cab47ac4-33f1-c984-428b-08dca7f6388d',
					senderTitle: 'UUX.Widget.Share',

					contextId: [this.widgetInstance.id],
					contextTitle: [this.widgetInstance.title],
					contextTypeId: ['5e1a3dee-f43d-ef11-47be-c403a89c6909'],
					contextTypeTitle: ['PDRWidgetInstanceType'],
				});
			},
		};
	}

	private initActions() {
		if (this.actions.length) {
			return;
		}

		const actions: WidgetInstanceAction[] = [
			{
				icon: 'content_copy',
				label: this.i18n.get('shell.dashboards.instance.clone'),
				onItemClick: async () => await this.page.copyWidgetInstance(this.widgetInstance),
			},
			{
				icon: 'copy_square',
				label: this.i18n.get('shell.dashboards.instance.copyTo'),
				children: this.dashboardNavigationItems.map(
					(targetNavItem): WidgetInstanceAction => ({
						icon: targetNavItem.icon ?? 'dashboard',
						label: targetNavItem.title,
						onItemClick: async () => {
							await this.dashboardService.copyWidgetInstance(this.widgetInstance.id, targetNavItem.id);
							this.notificationService.success(this.i18n.getFormatted('shell.dashboards.instance.copied', this.widgetInstance.title, targetNavItem.title));
						},
					})
				),
			},
		];

		if (this.widgetInstance.isDevMode && this.widgetInstance.widgetId) {
			actions.push({
				icon: 'widgets',
				label: this.i18n.get('shell.common.changeLayout'),
				onItemClick: () => this.dashboardService.changeWidgetInstanceLayout(this.widgetInstance),
			});
		}

		this.getShareAction().then((a) => actions.push(a));

		if (this.widgetInstance.isEditable) {
			actions.push({
				icon: 'shortcut',
				label: this.i18n.get('shell.dashboards.instance.moveTo'),
				children: this.dashboardNavigationItems.map(
					(targetNavItem): WidgetInstanceAction => ({
						icon: targetNavItem.icon ?? 'dashboard',
						label: targetNavItem.title,
						onItemClick: async () => {
							await this.page.moveWidgetInstance(this.widgetInstance, targetNavItem.id);
							this.notificationService.success(this.i18n.getFormatted('shell.dashboards.instance.moved', this.widgetInstance.title, targetNavItem.title));
						},
					})
				),
			});

			actions.push({
				icon: 'delete',
				label: this.i18n.get('solutionBuilderAgent.common.remove'),
				onItemClick: async () => {
					try {
						await this.notificationService.confirm(
							this.i18n.get('shell.dashboards.removeConfirmationTitle'),
							this.i18n.get('shell.dashboards.removeConfirmationPrompt')
						);
						await this.page.removeWidgetInstance(this.widgetInstance);
						this.notificationService.success(this.i18n.get('shell.dashboards.removeNotification'));
					} catch {
						// in case of cancel
					}
				},
			});
		}

		this.actions = actions;
	}

	public adjustDropdownPosition() {
		setTimeout(() => {
			const dropdown = $('.md-open-menu-container.md-active:visible');
			const dropdownHeight = dropdown.height() || 0;
			const dropdownTop = dropdown.offset()?.top || 0;
			const windowHieght = window.innerHeight;
			if (dropdownTop + dropdownHeight > windowHieght) {
				dropdown.css('margin-top', `${windowHieght - dropdownTop - dropdownHeight - 16}px`);
			}
		}, 150);
	}
}

export const WidgetInstanceActionsComponent = {
	name: 'mxWidgetInstanceActions',
	controller: WidgetInstanceActionsController,
	template,
	controllerAs: 'vm',
	bindings: {
		widgetInstance: '<',
	},
	require: {
		page: `^${DashboardPageComponent.name}`,
	},
};
